/**
 * Zod validation for imported flythrough JSON.
 *
 * Import is the only untrusted entry point, so everything optional gets a
 * `.default()`. That doubles as the forward-compatibility story: a file written
 * by an older build (or by HangarMax, whose waypoints carry `duration` and a
 * per-waypoint `easing` instead of `durationMs`/`holdMs`) still loads, with the
 * missing fields filled in rather than rejected.
 */
import { z } from 'zod'
import type { FlythroughDoc, FlythroughPlaylist } from './types'

const lngLat = z.tuple([z.number().finite(), z.number().finite()])

const waypointSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().default('Waypoint'),
    center: lngLat,
    zoom: z.number().finite().min(0).max(22),
    pitch: z.number().finite().min(0).max(85).default(0),
    bearing: z.number().finite().default(0),
    // HangarMax writes `duration`; we write `durationMs`. Accept either.
    durationMs: z.number().finite().min(0).optional(),
    duration: z.number().finite().min(0).optional(),
    holdMs: z.number().finite().min(0).default(0),
    easing: z.enum(['linear', 'ease-in', 'ease-out', 'ease-in-out']).optional(),
  })
  .transform((w) => ({
    id: w.id ?? makeId('wp'),
    name: w.name,
    center: [w.center[0], w.center[1]] as [number, number],
    zoom: w.zoom,
    pitch: w.pitch,
    bearing: w.bearing,
    durationMs: w.durationMs ?? w.duration ?? 3000,
    holdMs: w.holdMs,
    easing: w.easing,
  }))

const playlistSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().default('Tour'),
    waypoints: z.array(waypointSchema).default([]),
    loop: z.boolean().default(false),
    hideUi: z.boolean().default(false),
    rampMs: z.number().finite().min(0).default(800),
    speed: z.number().finite().min(0.05).max(8).default(1),
    restoreCameraOnStop: z.boolean().default(true),
  })
  .transform((p) => ({ ...p, id: p.id ?? makeId('fly') }))

const docSchema = z.object({
  kind: z.literal('planmax.flythrough'),
  version: z.number().int().positive(),
  playlists: z.array(playlistSchema),
})

/** Short unique id — mirrors `demo/masters-planner/data/taxiGraph.ts`. */
export function makeId(prefix: string): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  return `${prefix}_${uuid.slice(0, 8)}`
}

export interface ParseResult {
  ok: boolean
  playlists: FlythroughPlaylist[]
  error?: string
}

/**
 * Parse a `.json` flythrough export. Never throws — the caller surfaces
 * `error` in a toast.
 */
export function parseFlythroughDoc(raw: unknown): ParseResult {
  const result = docSchema.safeParse(raw)
  if (!result.success) {
    const first = result.error.issues[0]
    const where = first?.path.length ? ` at ${first.path.join('.')}` : ''
    return { ok: false, playlists: [], error: `${first?.message ?? 'Invalid file'}${where}` }
  }
  // Re-id on import so a re-import never silently overwrites an existing tour.
  const playlists = result.data.playlists.map((p) => ({ ...p, id: makeId('fly') }))
  return { ok: true, playlists }
}

export function serializeFlythroughDoc(playlists: FlythroughPlaylist[]): FlythroughDoc {
  return { kind: 'planmax.flythrough', version: 1, playlists }
}
